import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { UNIVERSAL_CACHE_KEYS } from './cache-keys';

/**
 * Fetches all content from the universal_storage_items table,
 * and joins the main data for each content type.
 */
export interface UniversalLibraryItemRecord {
  id: string;
  content_type: string;
  created_at: string;
  category_id: string;
  display_order?: number;
  // Foreign key columns
  workbook_image_id?: string;
  workbook_research_id?: string;
  workbook_page_id?: string;
  // Content data from joined tables
  image_data?: any;
  research_data?: any;
  workbook_data?: any;
  private_image_data?: any;
  private_research_data?: any;
  private_page_data?: any;
  // 🎯 NEW CONTENT TYPE DATA CAN BE ADDED HERE
  // Example: new_type_data?: any;
}

// Use the optimized PostgreSQL function instead of manual joins
async function fetchUniversalStorageItems({ 
  contentTypes, 
  categoryId 
}: { 
  contentTypes?: string[];
  categoryId?: string | null;
}) {
  console.log('[useUniversalLibraryItems] Fetching with RPC function:', {
    contentTypes,
    categoryId,
    timestamp: new Date().toISOString()
  });

  // Use the PostgreSQL function
  const { data, error } = await supabase
    .rpc('get_universal_storage_items', {
      p_content_types: contentTypes || null,
      p_category_id: categoryId || null
    });

  if (error) {
    console.error('[useUniversalLibraryItems] RPC function error:', error);
    throw error;
  }

  if (!data || data.length === 0) {
    console.log('[useUniversalLibraryItems] No data returned from RPC function');
    return [];
  }

  console.log('[useUniversalLibraryItems] RPC function returned:', {
    count: data.length,
    items: data,
    timestamp: new Date().toISOString()
  });

  // The PostgreSQL function already returns data in the correct format
  const records: UniversalLibraryItemRecord[] = data.map((row: any) => ({
    id: row.id,
    content_type: row.content_type,
    created_at: row.created_at,
    category_id: row.category_id,
    display_order: row.display_order || 0,
    // Foreign key columns
    workbook_image_id: row.workbook_image_id || undefined,
    workbook_research_id: row.workbook_research_id || undefined,
    workbook_page_id: row.workbook_page_id || undefined,
    // Content data
    image_data: row.image_data || undefined,
    research_data: row.research_data || undefined,
    workbook_data: row.workbook_data || undefined,
    private_image_data: row.private_image_data || undefined,
    private_research_data: row.private_research_data || undefined,
    private_page_data: row.private_page_data || undefined,
  }));
  
  console.log('[useUniversalLibraryItems] Final processed records:', {
    count: records.length,
    timestamp: new Date().toISOString()
  });

  return records;
}

export function useUniversalStorageItems({ 
  contentTypes, 
  categoryId 
}: { 
  contentTypes?: string[];
  categoryId?: string | null;
} = {}) {
  return useQuery({
    queryKey: UNIVERSAL_CACHE_KEYS.FILTERED_STORAGE(contentTypes, categoryId),
    queryFn: () => fetchUniversalStorageItems({ contentTypes, categoryId }),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
